import React, { useContext, useMemo, useEffect, useCallback } from 'react';
import { findDOMNode } from 'react-dom';
import { List, AutoSizer } from 'react-virtualized';
import { observer } from 'mobx-react-lite';
import { Draggable, Droppable, } from 'react-beautiful-dnd';
import isFunction from 'lodash/isFunction';
// import noop from 'lodash/noop';
import { Col } from '../../../lib/grid';
import classnames from 'classnames';
import QuoteItem from './QuoteItem';
import DataSet from '../data-set';
import BoardContext from './BoardContext';
import { ViewField } from './enum';
import Button from '../button';
import { FuncType } from '../button/interface';
export const getBackgroundColor = (isDraggingOver, isDraggingFrom) => {
    if (isDraggingOver) {
        return 'rgb(222, 235, 255)';
    }
    if (isDraggingFrom) {
        return "rgb(255, 250, 230)";
    }
    return "white";
};
export const getDraggingStyle = (isDraggingOver, isDraggingFrom) => {
    if (isDraggingOver) {
        return {
            background: 'rgba(8,64,248,0.02)',
            border: '1px dashed #82ACFF',
            borderRadius: '2px',
        };
    }
    if (isDraggingFrom) {
        return {
            background: 'rgba(8,64,248,0.02)',
            border: '1px dashed #82ACFF',
            borderRadius: '2px',
        };
    }
    return {
        background: 'white',
    };
};
// Using a higher order function so that we can look up the quotes data to retrieve
// our quote from within the rowRender function
const getRowRender = (quotes, columnId, prefixCls, viewProps, listDragging, command, renderCommand, commandsLimit, dataSet, kanbanDS, displayFields, isDragDropDisabled, draggableProps) => ({ index, style }) => {
    const quote = quotes.get(index);
    const isLast = quotes && quotes.length === (index + 1);
    // We are rendering an extra item for the placeholder
    // Do do this we increased our data set size to include one 'fake' item
    if (!quote) {
        return null;
    }
    // Faking some nice spacing around the items
    const patchedStyle = {
        ...style,
        left: Number(style.left) + 8,
        top: Number(style.top) + 8,
        width: `calc(${style.width} - ${8 * 2}px)`,
        height: Number(style.height) - (isLast && !listDragging ? 46 : 8),
    };
    const btnStyle = {
        ...style,
        left: Number(style.left) + 8,
        top: Number(style.top) + Number(style.height) - 28,
        width: `calc(${style.width} - ${8 * 2}px)`,
        maxWidth: 'unset',
        height: 28,
    };
    const restDraggableProps = () => {
        if (isFunction(draggableProps)) {
            return draggableProps({ columnDS: quotes, dataSet });
        }
        return draggableProps;
    };
    return (React.createElement(Draggable, Object.assign({ draggableId: String(quote.id), index: index, key: quote.id, isDragDisabled: isDragDropDisabled && true }, restDraggableProps()), (provided, snapshot) => (React.createElement(React.Fragment, null,
        React.createElement(QuoteItem, { command: command, renderCommand: renderCommand, commandsLimit: commandsLimit, provided: provided, quote: quote, isDragging: snapshot.isDragging, style: patchedStyle, prefixCls: prefixCls, viewProps: viewProps, displayFields: displayFields, index: index, dataSet: quotes }),
        (isLast && (kanbanDS.getState(`${columnId}_totalCount`) || quotes.totalCount) > quotes.length) ? (React.createElement(Button, { onClick: async () => {
                await quotes.queryMore(quotes.currentPage + 1, undefined);
                kanbanDS.setState(`${columnId}_totalCount`, undefined);
            }, style: {
                ...btnStyle,
                maxWidth: 'unset',
            }, hidden: listDragging, funcType: FuncType.flat }, "\u52A0\u8F7D\u66F4\u591A")) : null))));
};
const KanbanColumn = function KanbanColumn(props) {
    const { columnId, quotes = [], prefixCls, className, header, style, kanbanProps, kanbanDS, groupingBy } = props;
    const { droppableProps, draggableProps, isDragDropDisabled } = kanbanProps;
    const { displayFields, customizedDS, command, renderCommand, dataSet, commandsLimit } = useContext(BoardContext);
    const viewProps = useMemo(() => customizedDS.current.get(ViewField.viewProps) || {}, [customizedDS.current]);
    const columnDS = useMemo(() => new DataSet({
        ...dataSet.props,
        queryParameter: {
            [groupingBy]: columnId === '_empty' ? 'null' : columnId,
        },
        data: quotes.slice(),
        autoQuery: false,
        ...kanbanProps.columnDsProps,
    }), [quotes]);
    const headHeight = useMemo(() => {
        return columnDS.getState('__HEADHEIGHT__') || 30;
    }, [columnDS, columnDS.getState('__HEADHEIGHT__'), commandsLimit, command, renderCommand]);
    const defaultRowHeight = useMemo(() => (viewProps[ViewField.displayFields] ? viewProps[ViewField.displayFields].length : 1) * 24 + Math.max(30, headHeight) + 24, [viewProps[ViewField.displayFields] && viewProps[ViewField.displayFields].length, headHeight]);
    const totalCount = useMemo(() => Math.max(kanbanDS.getState(`${columnId}_totalCount`) || 0, columnDS.totalCount), [columnId, columnDS.totalCount]);
    useEffect(() => {
        if (kanbanDS) {
            kanbanDS.setState(`${columnId.toString().toLocaleUpperCase()}_COLUMNDS`, columnDS);
        }
    }, [kanbanDS, columnDS]);
    const restDroppableProps = useCallback(() => {
        if (isFunction(droppableProps)) {
            return droppableProps({ columnDS, dataSet });
        }
        return droppableProps;
    }, []);
    const cls = classnames(`${prefixCls}-column-container`, {}, className);
    return (React.createElement(Col, { span: viewProps[ViewField.cardWidth] || 6, "data-key": viewProps[ViewField.displayFields] && viewProps[ViewField.displayFields].length, style: style, className: cls },
        React.createElement("div", { className: `${prefixCls}-column-header` },
            React.createElement("span", null, header),
            React.createElement("span", { className: `${prefixCls}-column-header-count` },
                "(",
                columnDS ? totalCount : 0,
                ")")),
        React.createElement(Droppable, Object.assign({ droppableId: columnId.toString(), mode: "virtual", renderClone: (provided, snapshot, rubric) => (React.createElement(QuoteItem, { prefixCls: prefixCls, provided: provided, isDragging: snapshot.isDragging, quote: columnDS.get(rubric.source.index), style: { margin: 0 }, viewProps: viewProps, displayFields: displayFields, command: command, renderCommand: renderCommand, commandsLimit: commandsLimit, dataSet: columnDS })), isDropDisabled: isDragDropDisabled && true }, restDroppableProps()), (droppableProvided, snapshot) => {
            const itemCount = columnDS ? snapshot.isUsingPlaceholder
                ? columnDS.length + 1
                : columnDS.length : 0;
            return (React.createElement(AutoSizer, { disableHeight: true }, ({ width }) => (React.createElement(List, { className: `${prefixCls}-column-list`, height: viewProps.viewHeight || 366, rowCount: itemCount, rowHeight: ({ index }) => (itemCount - 1) === index ? defaultRowHeight + 36 : defaultRowHeight, width: width, ref: (ref) => {
                    // react-virtualized has no way to get the list's ref that I can so
                    // So we use the `ReactDOM.findDOMNode(ref)` escape hatch to get the ref
                    // listRef = ref;
                    if (ref) {
                        ref.recomputeRowHeights();
                        // columnDS.setState('__REF__', ref);
                        // eslint-disable-next-line react/no-find-dom-node
                        const nodeRef = findDOMNode(ref);
                        if (nodeRef instanceof HTMLElement) {
                            droppableProvided.innerRef(nodeRef);
                        }
                    }
                }, style: getDraggingStyle(snapshot.isDraggingOver, Boolean(snapshot.draggingFromThisWith)), 
                // style={{
                //   backgroundColor: getBackgroundColor(
                //     snapshot.isDraggingOver,
                //     Boolean(snapshot.draggingFromThisWith),
                //   ),
                //   transition: 'background-color 0.2s ease',
                // }}
                rowRenderer: columnDS ? getRowRender(columnDS, columnId, prefixCls, viewProps, snapshot.isDraggingOver || Boolean(snapshot.draggingFromThisWith), command, renderCommand, commandsLimit, dataSet, kanbanDS, displayFields, isDragDropDisabled, draggableProps) : null }))));
        })));
};
KanbanColumn.displayName = 'KanbanColumn';
export default observer(KanbanColumn);
